import { formatTrackingLogToDescription } from '../pages/private/reports/vesselsSection/formatTrackingLogToDescription';
import {
  Docking,
  Docking as DockingType,
  DockingObservationType,
  GenericResponse,
  TrackingLogType,
} from '../types';
import { getHeaders, tosApi } from './tosApi';
import { cleanParams, getInvalidatesTags, TagType } from './utils';

type DockingExportXmlParamsType = {
  initial_date_report?: string;
  final_date_report?: string;
  list_of_docking_ids?: number[];
};

/**
 * Os dois estados em que `GET /maritime/dockings` sabe separar os eventos de
 * uma escala. Concluído é o que o `is_done()` ou o `is_canceled()` do domínio
 * do backend reconhece; o resto é não concluído.
 */
export type DockingEventState = 'not_concluded' | 'concluded';

export type StopoverDockingsParamsType = {
  stopover?: number;
  event_state: DockingEventState;
  page?: number;
};

/**
 * A tag das duas listagens por estado do evento.
 *
 * Ela é `id`, e não `code`, porque o RTK Query casa tag com tag por `type` e
 * `id` e ignora qualquer outra chave. As duas queries provêm a mesma tag de
 * propósito: um evento que se conclui sai de um bloco e entra no outro, então
 * uma mutação de atracação tem de invalidar os dois.
 */
export const STOPOVER_DOCKINGS_TAG_ID = 'stopover-dockings-by-event-state';

export type TrackingLogParamsType = {
  initial_date_report?: string;
  final_date_report?: string;
  status?: string;
  vessel_name_or_docking_code?: string;
  page?: number;
};

const OBSERVATIONS_URL = (docking_id: number) =>
  `/maritime/dockings/${docking_id}/observations`;

const observationsTagCode = (docking_id: number) =>
  `docking-observations-${docking_id}`;

export type DockingObservationToSaveType = {
  docking_id: number;
  observation: string;
};

// Além do histórico do modal, a grid de Relatórios precisa revalidar para o
// ícone de observação da linha acompanhar.
const observationsInvalidatesTags = (docking_id: number): TagType[] => [
  { type: 'Docking', code: observationsTagCode(docking_id) },
  { type: 'Stopover', code: '5' },
];

export const dockingApi = tosApi.injectEndpoints({
  endpoints: (build) => ({
    getDockings: build.query<Array<DockingType>, void>({
      query: () => {
        return {
          url: `/maritime/dockings`,
          method: 'GET',
          headers: getHeaders,
        };
      },
      transformResponse: (response: { results: Array<DockingType> }) => {
        response.results.forEach((docking: any) => {
          docking.key = docking.id;
        });
        return response.results;
      },
    }),
    getStopoverDockings: build.query<
      GenericResponse & { results: Array<DockingType> },
      StopoverDockingsParamsType
    >({
      query: (params) => {
        return {
          url: `/maritime/dockings`,
          method: 'GET',
          headers: getHeaders,
          params: cleanParams(params),
        };
      },
      // Sem transformResponse: a ordem exibida é a que o backend devolve.
      providesTags: () => [{ type: 'Docking', id: STOPOVER_DOCKINGS_TAG_ID }],
    }),
    getDockingAlerts: build.query<
      Array<any>,
      { id?: number; berthId?: string }
    >({
      query: (arg) => {
        const { id, berthId } = arg;
        return {
          url: `/maritime/dockings/${id}/check-alerts/${berthId}`,
          method: 'GET',
          headers: getHeaders,
        };
      },
    }),
    getDockingsByDate: build.query<
      { results: Array<any> },
      {
        initial_expected_berthing?: string;
        final_expected_berthing?: string;
        name_or_imo?: string;
        order_by?: string;
      }
    >({
      query: (arg) => {
        return {
          url: `/maritime/dockings-window`,
          method: 'GET',
          headers: getHeaders,
          params: arg,
        };
      },
      transformResponse: (response: { results: Array<any> }) => {
        const transformedResponse = { ...response };
        transformedResponse.results.forEach((element) => {
          if (element.stopover.port_stay_finished !== null) {
            element.docking_window_status = 'CARRIED_OUT';
          }
        });
        return response;
      },
      providesTags: () => [{ type: 'Docking', code: 'docking-window' }],
    }),
    updateDockingWindow: build.mutation<
      any,
      {
        id: number;
        expected_berthing?: string;
        expected_unberthing?: string;
        expected_anchoring?: string;
        expected_unanchoring?: string;
        docking_place?: string;
        pilot_expected_on_board?: string;
        pilot_expected_leaving_on_board?: string;
        pilot_expected_on_board_undocking?: string;
        pilot_expected_leaving_on_board_undocking?: string;
      }
    >({
      query: ({ id, ...update }) => {
        return {
          url: `/maritime/dockings-window/${id}`,
          method: 'PATCH',
          body: update,
        };
      },
    }),
    updateDocking: build.mutation<any, any>({
      query: (update) => {
        return {
          url: `/maritime/dockings/${update.id}`,
          method: 'PUT',
          body: update,
        };
      },
      invalidatesTags: (_result, error) =>
        getInvalidatesTags(error, [
          { type: 'Stopover', code: '1' },
          { type: 'Stopover', code: '3' },
          { type: 'Manoeuvre', code: '1' },
          { type: 'StopoverLog', code: '1' },
          { type: 'Docking', code: 'docking-window' },
          { type: 'Docking', id: STOPOVER_DOCKINGS_TAG_ID },
        ]),
    }),
    createDocking: build.mutation<any, any>({
      query: (docking) => {
        return {
          url: '/maritime/dockings',
          method: 'POST',
          body: docking,
        };
      },
      invalidatesTags: (_result, error) =>
        getInvalidatesTags(error, [
          { type: 'Stopover', code: '1' },
          { type: 'Stopover', code: '3' },
          { type: 'Docking', code: 'docking-window' },
          { type: 'Docking', id: STOPOVER_DOCKINGS_TAG_ID },
        ]),
    }),
    patchDocking: build.mutation<any, any>({
      query: (docking) => {
        const dockingToSave = { ...docking };
        delete dockingToSave.id;
        delete dockingToSave.notInvalidateTags;
        return {
          url: `/maritime/dockings/${docking.id}`,
          method: 'PATCH',
          body: dockingToSave,
        };
      },
      invalidatesTags: (_result, error, queryArgs) =>
        queryArgs.notInvalidateTags
          ? []
          : getInvalidatesTags(error, [
              { type: 'StopoverLog', code: '1' },
              { type: 'Stopover', code: '1' },
              { type: 'Stopover', code: '3' },
              { type: 'Docking', code: 'docking-window' },
              { type: 'Docking', id: STOPOVER_DOCKINGS_TAG_ID },
            ]),
    }),
    cancelDocking: build.mutation<DockingType, { id: number }>({
      query: (data) => {
        const { id } = data;
        return {
          url: `/maritime/dockings/${id}/cancel`,
          method: 'PATCH',
        };
      },
      invalidatesTags: (result, error) =>
        getInvalidatesTags(error, [
          { type: 'StopoverLog', code: '1' },
          { type: 'Stopover', code: result?.stopover_id },
          { type: 'Docking', code: 'docking-window' },
          { type: 'Docking', id: STOPOVER_DOCKINGS_TAG_ID },
        ]),
    }),
    cancelDockingDraft: build.mutation<DockingType, { id: number }>({
      query: (data) => {
        const { id } = data;
        return {
          url: `/maritime/dockings/draft/${id}/cancel`,
          method: 'PATCH',
        };
      },
      invalidatesTags: (result, error) =>
        getInvalidatesTags(error, [
          { type: 'StopoverLog', code: '1' },
          { type: 'Stopover', code: result?.stopover_id },
          { type: 'Docking', code: 'docking-window' },
          { type: 'Docking', code: 'docking-draft' },
          { type: 'Docking', id: STOPOVER_DOCKINGS_TAG_ID },
        ]),
    }),
    confirmDockingDraft: build.mutation<DockingType, { id: number }>({
      query: (data) => {
        const { id } = data;
        return {
          url: `/maritime/dockings/draft/${id}/confirm`,
          method: 'PATCH',
        };
      },
      invalidatesTags: (result, error) =>
        getInvalidatesTags(error, [
          { type: 'StopoverLog', code: '1' },
          { type: 'Stopover', code: result?.stopover_id },
          { type: 'Docking', code: 'docking-window' },
          { type: 'Docking', code: 'docking-draft' },
          { type: 'Docking', id: STOPOVER_DOCKINGS_TAG_ID },
        ]),
    }),
    checkBerthAvailable: build.mutation<
      Docking[],
      { tag: string; date?: string; id?: number }
    >({
      query: (data) => {
        const dataToQuery = { ...data };
        delete dataToQuery.id;
        return {
          url: `/maritime/dockings/${data.id}/check-available`,
          method: 'POST',
          body: dataToQuery,
        };
      },
    }),
    exportXml: build.mutation<string, DockingExportXmlParamsType>({
      query: (body) => {
        return {
          url: '/maritime/dockings/export-xml',
          method: 'PUT',
          body,
          responseHandler: (response) =>
            response.ok ? response.text() : response.json(),
        };
      },
      invalidatesTags: (_result, error) =>
        getInvalidatesTags(error, [{ type: 'Stopover', code: '5' }]),
    }),
    trackingLog: build.query<
      GenericResponse & { results: TrackingLogType[] },
      void | TrackingLogParamsType
    >({
      query: (params) => {
        return {
          url: '/maritime/dockings/tracking-log',
          method: 'GET',
          params: params || {},
        };
      },
      transformResponse: (
        baseResponse: GenericResponse & { results: TrackingLogType[] }
      ) => {
        return {
          ...baseResponse,
          results: baseResponse.results.map((item, index) => {
            return formatTrackingLogToDescription(item, index);
          }),
        };
      },
    }),
    createDockingCharge: build.mutation<any, number>({
      query: (docking_id) => {
        return {
          url: `/maritime/dockings/${docking_id}/create-charge`,
          method: 'POST',
        };
      },
      invalidatesTags: (_result, error) =>
        getInvalidatesTags(error, [
          { type: 'Stopover', code: '5' },
          { type: 'Charge', code: '1' },
        ]),
    }),
    // A listagem responde um array puro, sem o envelope count/next/previous/results
    // das demais listas, já ordenado do mais recente para o mais antigo.
    getDockingObservations: build.query<Array<DockingObservationType>, number>({
      query: (docking_id) => {
        return {
          url: OBSERVATIONS_URL(docking_id),
          method: 'GET',
          headers: getHeaders,
        };
      },
      providesTags: (_result, _error, docking_id) => [
        { type: 'Docking', code: observationsTagCode(docking_id) },
      ],
    }),
    createDockingObservation: build.mutation<
      DockingObservationType,
      DockingObservationToSaveType
    >({
      query: ({ docking_id, observation }) => {
        return {
          url: OBSERVATIONS_URL(docking_id),
          method: 'POST',
          body: { observation },
        };
      },
      invalidatesTags: (_result, error, { docking_id }) =>
        getInvalidatesTags(error, observationsInvalidatesTags(docking_id)),
    }),
    updateDockingObservation: build.mutation<
      DockingObservationType,
      DockingObservationToSaveType & { observation_id: number }
    >({
      query: ({ docking_id, observation_id, observation }) => {
        return {
          url: `${OBSERVATIONS_URL(docking_id)}/${observation_id}`,
          method: 'PUT',
          body: { observation },
        };
      },
      invalidatesTags: (_result, error, { docking_id }) =>
        getInvalidatesTags(error, observationsInvalidatesTags(docking_id)),
    }),
    deleteDockingObservation: build.mutation<
      void,
      { docking_id: number; observation_id: number }
    >({
      query: ({ docking_id, observation_id }) => {
        return {
          url: `${OBSERVATIONS_URL(docking_id)}/${observation_id}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: (_result, error, { docking_id }) =>
        getInvalidatesTags(error, observationsInvalidatesTags(docking_id)),
    }),
    getDockingsDraft: build.query<
      { results: Array<any> },
      {
        initial_expected_berthing?: string;
        final_expected_berthing?: string;
        name_or_imo?: string;
        order_by?: string;
      }
    >({
      query: (arg) => {
        return {
          url: `/maritime/dockings/draft`,
          method: 'GET',
          headers: getHeaders,
          params: arg,
        };
      },
      providesTags: () => [{ type: 'Docking', code: 'docking-draft' }],
    }),
  }),
  overrideExisting: true,
});

export const {
  useGetDockingsQuery,
  useGetStopoverDockingsQuery,
  useGetDockingAlertsQuery,
  useGetDockingsByDateQuery,
  useGetDockingsDraftQuery,
  useUpdateDockingWindowMutation,
  useCreateDockingMutation,
  useUpdateDockingMutation,
  usePatchDockingMutation,
  useConfirmDockingDraftMutation,
  useCancelDockingDraftMutation,
  useCancelDockingMutation,
  useCheckBerthAvailableMutation,
  useExportXmlMutation,
  useTrackingLogQuery,
  useCreateDockingChargeMutation,
  useGetDockingObservationsQuery,
  useCreateDockingObservationMutation,
  useUpdateDockingObservationMutation,
  useDeleteDockingObservationMutation,
} = dockingApi;
